import {Box} from '@mui/material';
import {Children, cloneElement, FC, isValidElement, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {MapSx} from './Map.sx';
import {MapMarker, MapType} from './Map.types';
import {useDeepCompareEffectForMaps} from 'src/app/hooks';
import {MarkerClusterer} from '@googlemaps/markerclusterer';
import theme from 'src/theme';
import {ClusterMark} from '../ClusterMark';

/**
 * What a pin opens: the place's name, a line under it such as the address, and a link to its page.
 *
 * Built as DOM rather than an HTML string, so a name is only ever text - a location called
 * "<img onerror=...>" stays a name. The link keeps its href, so a middle click or a new tab works, and
 * navigates inside the app on a plain click.
 */
const infoContent = (marker: MapMarker, open: (href: string) => void): HTMLElement => {
    const root = document.createElement('div');
    root.style.cssText = `font-family: ${theme.typography.fontFamily}; padding: 2px 4px; max-width: 240px;`;

    const name = document.createElement('div');
    name.textContent = marker.title ?? '';
    name.style.cssText = `font-size: 14px; font-weight: 600; color: ${theme.palette.text.primary};`;
    root.appendChild(name);

    if (marker.subtitle) {
        const subtitle = document.createElement('div');
        subtitle.textContent = marker.subtitle;
        subtitle.style.cssText = `margin-top: 2px; font-size: 12px; color: ${theme.palette.neutral[600]};`;
        root.appendChild(subtitle);
    }

    if (marker.link) {
        const {href, label} = marker.link;
        const link = document.createElement('a');
        link.href = href;
        link.textContent = `${label} →`;
        link.style.cssText = `display: inline-block; margin-top: 6px; font-size: 13px; font-weight: 600; `
            + `color: ${theme.palette.primary.main}; text-decoration: none;`;
        link.addEventListener('click', (event) => {
            if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
                event.preventDefault();
                open(href);
            }
        });
        root.appendChild(link);
    }

    return root;
};

export const Map: FC<MapType> = ({
                                     onClick,
                                     children,
                                     style,
                                     markers,
                                     clusterization,
                                     ...options
                                 }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<google.maps.Map>();
    const infoWindow = useRef<google.maps.InfoWindow>();
    // Read at click time rather than captured: the markers are built once per set of coordinates and
    // must not be rebuilt because the router handed over a new function.
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;

    useEffect(() => {
        if (ref.current && !map) {
            setMap(new window.google.maps.Map(ref.current, {}));
        }
    }, [ref, map]);

    // Once per set of coordinates, not once per render. These are live Google Maps objects, and they
    // were being built in the render body: a page showing two thousand locations allocated two
    // thousand markers and an info window every time anything above it re-rendered, and the previous
    // batch stayed attached to the map with nothing left holding a reference to detach it.
    const marks = useMemo(
        () =>
            markers?.map((point) => {
                // A pin that names its place answers a click. One that ignored clicks let them fall
                // through to the base map, where a Google place icon under the pin opened Google's own
                // card - "unable to load information about this place" - instead of ours.
                const opensInfo = Boolean(point.title);
                const marker = new google.maps.Marker({
                    position: {lat: point.lat, lng: point.lng},
                    clickable: opensInfo,
                    title: point.title,
                    icon: '/static/images/PinMap.png',
                });

                if (opensInfo) {
                    marker.addListener('click', () => {
                        if (!infoWindow.current) {
                            infoWindow.current = new google.maps.InfoWindow();
                        }
                        infoWindow.current.setContent(infoContent(point, (href) => {
                            infoWindow.current?.close();
                            navigateRef.current(href);
                        }));
                        infoWindow.current.open(marker.getMap(), marker);
                    });
                }

                return marker;
            }),
        [markers],
    );

    useEffect(() => {
        if (map) {
            google.maps.event.clearListeners(map, 'click');

            if (onClick) {
                map.addListener('click', onClick);
            }
        }
    }, [map, onClick]);

    // Deliberately not in the effect above. That one has to follow onClick, which is a new function
    // on every render of the parent; the clusterer must not. Sharing an effect meant a fresh
    // clusterer - carrying its own copy of every marker - on each render, none of them ever removed,
    // so the map grew heavier the longer the session went on rather than only while data loaded.
    useEffect(() => {
        if (!map || !clusterization) {
            return;
        }

        const clusterer = new MarkerClusterer({markers: marks, map, renderer: {render: ClusterMark}});

        return () => {
            clusterer.clearMarkers();
            clusterer.setMap(null);
        };
    }, [map, marks, clusterization]);

    // Without a clusterer the pins go on the map themselves - the same markers, so a pin on a location's
    // own page answers a click the way one on the overview does. They used to be drawn by a separate
    // component that knew nothing about titles, which is why that pin stayed mute.
    useEffect(() => {
        if (!map || clusterization || !marks) {
            return;
        }

        marks.forEach((marker) => marker.setMap(map));

        return () => {
            marks.forEach((marker) => marker.setMap(null));
        };
    }, [map, marks, clusterization]);

    useDeepCompareEffectForMaps(() => {
        if (map) {
            map.setOptions(options);
        }
    }, [map, options]);

    return (
        <>
            <Box
                ref={ref}
                sx={MapSx}
            />
            {Children.map(children, (child) => {
                if (isValidElement(child)) {
                    return cloneElement(child, {map});
                }
            })}
        </>
    );
};
